import { API } from './instances'

// Server-sent event names emitted while a statement is processed.
export const UPLOAD_EVENTS = {
  UPLOADED: 'UPLOADED',
  EXTRACTING_INVESTMENTS: 'EXTRACTING_INVESTMENTS',
  PROCESSING_INVESTMENTS: 'PROCESSING_INVESTMENTS',
  DOCUMENT_COMPLETED: 'DOCUMENT_COMPLETED',
  DOCUMENT_FAILED: 'DOCUMENT_FAILED',
}

// The document is read and processed server-side, so the request stays open well past a
// normal API call. Processing scales with the number of investments found; 50+ holdings
// consistently run past 90 seconds. An inactivity timer in the caller ends stalled uploads,
// so this is only a backstop.
const UPLOAD_TIMEOUT = 150000

// The events that end a document's processing. Nothing useful follows them, and a server that
// leaves the stream open would otherwise hold the reader until the inactivity timeout.
const TERMINAL_EVENTS = [UPLOAD_EVENTS.DOCUMENT_COMPLETED, UPLOAD_EVENTS.DOCUMENT_FAILED]

// If the server goes quiet mid-stream, the request timeout above does not help: the response
// has already begun, so the connection just sits there and the user waits on a spinner that
// will never resolve. Give up if no event arrives for this long.
const INACTIVITY_TIMEOUT = 90000

const uploadConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
    'Accept': 'text/event-stream',
  },
  // The fetch adapter is required for a streamed response body; the default XHR
  // adapter buffers the whole response and we would only see events at the end.
  adapter: 'fetch',
  responseType: 'stream',
  timeout: UPLOAD_TIMEOUT,
}

/**
 * Posts a PDF statement and returns the raw event stream.
 *
 * @param {object} params
 * @param {Blob} params.file
 * @param {number|string} params.investmentAccountId
 * @param {boolean} [params.institutional] Institutional accounts use a separate endpoint.
 * @returns {Promise<ReadableStream>}
 */
export const uploadStatement = async ({ file, investmentAccountId, institutional }) => {
  const path = institutional ? 'investment-pdf/upload/institutional' : 'investment-pdf/upload/v2'
  const response = await API.post(
    path,
    { file, investmentAccountId: Number(investmentAccountId) },
    uploadConfig
  )

  return response.data
}

/** Rejects if `promise` does not settle before the inactivity timeout elapses. */
const withInactivityTimeout = promise => {
  let timer

  const expiry = new Promise((resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error('Upload stream stopped responding')),
      INACTIVITY_TIMEOUT
    )
  })

  return Promise.race([promise, expiry]).finally(() => clearTimeout(timer))
}

/**
 * Reads an upload's event stream, invoking `onEvent` for each event name.
 *
 * Events arrive as `event: NAME` lines. Any accompanying `data:` lines are ignored —
 * the event name alone carries the progress we surface.
 *
 * @param {ReadableStream} stream
 * @param {(eventName: string) => void} onEvent
 * @throws If the response is not a stream, or the server stops sending events.
 */
export const readUploadStream = async (stream, onEvent) => {
  if (!stream || typeof stream.getReader !== 'function') {
    throw new Error('Upload response is not a readable stream')
  }

  const reader = stream.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  /** @returns true once an event has ended the document's processing. */
  const handleLine = line => {
    if (!line.startsWith('event:')) return false

    const event = line.replace(/^event:\s*/, '').trim()
    onEvent(event)

    return TERMINAL_EVENTS.includes(event)
  }

  try {
    while (true) {
      const { value, done } = await withInactivityTimeout(reader.read())

      if (done) {
        if (buffer.trim()) handleLine(buffer.trim())
        return
      }

      if (!value) continue

      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      // The last element may be a partial line, so hold it back for the next chunk.
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (line.trim() && handleLine(line.trim())) return
      }
    }
  } finally {
    // Release the connection whether we finished, timed out, or the caller threw.
    reader.cancel?.().catch(() => {})
  }
}
